// The single owner of "what is currently making sound." Only one of
// {FilePlaybackEngine, MetronomeEngine} may be active at any time; starting
// either one always tears down the other first. This is enforced structurally
// here rather than by convention in UI code, so no future UI bug can create
// overlapping audio.
//
// The synthetic click layer (SyntheticClickLayer) is a deliberate exception
// to that exclusivity: it runs ALONGSIDE the file engine, not instead of it,
// because it's an independent per-song opt-in (generate a click for a file
// that has none), not a third playback mode. It has its own start/stop/
// reanchor lifecycle, driven in lockstep with the file engine's own
// play/pause/seek calls.

import type { SongRecord, MetronomeSettings, SetlistRouting } from '../core/types';
import { parseRehearsalRouting } from '../core/types';
import { database } from '../db/database';
import { FilePlaybackEngine, type FilePlaybackEvent } from './FilePlaybackEngine';
import { MetronomeEngine, type MetronomeEvent, type BeatEvent } from './MetronomeEngine';
export type { BeatEvent };
import { SyntheticClickLayer } from './SyntheticClickLayer';

/** Turns a setlist's single routing choice plus one song's own content
 * (does it have a track? an active click?) into the actual per-engine
 * output sides — the one place this collapse happens, so PlaybackController
 * and MarkerEditorPlayer (Library's mini-player, which always previews in
 * plain stereo — see its own call site) can't drift apart on the rules.
 * Routing is deliberately a SETLIST-level setting now, not stored per song
 * (see SetlistRouting's own doc comment for why) — a song only ever
 * determines whether it HAS a track/click to split, never which physical
 * side either one lands on.
 *
 * clickSide includes 'stereo' (unlike a plain 'left'/'right' pan) — the
 * 'stereo-click-*' routing family deliberately leaves the TRACK alone and
 * only moves the click, so the click itself needs to be able to sit in
 * both ears at once, not just hard-left/hard-right. */
export function resolveOutputSides(
  routing: SetlistRouting,
  hasTrack: boolean,
  hasClick: boolean,
): { trackSide: 'stereo' | 'left' | 'right'; clickSide: 'stereo' | 'left' | 'right' } {
  if (!hasClick) {
    // Nothing to protect from PA bleed (or, for the 'stereo-click-*'
    // family, nothing being split from the track in the first place) — a
    // track with no click just plays its own natural stereo mix.
    // clickSide is meaningless here (callers only read it alongside
    // hasClick), but still needs a concrete value to satisfy the type.
    return { trackSide: 'stereo', clickSide: 'stereo' };
  }
  const rehearsal = parseRehearsalRouting(routing);
  if (rehearsal) {
    // Rehearsal: track and click each independently take whichever side
    // was chosen — no PA to protect, so (unlike Live below) every one of
    // the 9 combinations is just honored directly, including narrowing the
    // track itself. A track-less song (metronome-only) has nothing to
    // narrow, so its side stays a no-op 'stereo' regardless of what was
    // picked, same reasoning Live's own hasTrack fallback already used.
    return { trackSide: hasTrack ? rehearsal.trackSide : 'stereo', clickSide: rehearsal.clickSide };
  }
  switch (routing) {
    case 'tracks-left-click-right':
      // A genuine PA/in-ear split needs a track to actually narrow — a
      // metronome-only song (hasTrack: false) has none, so its track side
      // stays a no-op 'stereo' and only its click side follows the split.
      return { trackSide: hasTrack ? 'left' : 'stereo', clickSide: 'right' };
    case 'tracks-right-click-left':
      return { trackSide: hasTrack ? 'right' : 'stereo', clickSide: 'left' };
    default:
      // Unreachable: routing is exhaustively either a RehearsalRouting
      // value (handled above via parseRehearsalRouting) or one of the two
      // Live literals just matched — TS's control-flow narrowing can't see
      // through the template-literal RehearsalRouting check, so this exists
      // purely to satisfy the compiler, not because this branch can execute.
      return { trackSide: 'stereo', clickSide: 'stereo' };
  }
}

/** Computes the ACTUAL pre-roll duration/beat-count a count-in would use,
 * from the same three inputs startPreRoll takes — factored out as a pure,
 * standalone function (rather than kept private inside startPreRoll) so a
 * static preview (e.g. the setlist panel showing "Count-in · 8 counts"
 * before Play is even pressed) can compute the exact same numbers without
 * duplicating this math and risking it drifting out of sync with what
 * startPreRoll actually does. See startPreRoll's own doc comment for the
 * full reasoning behind pickupBeats/effectiveBeats. */
export function computeCountInPreview(
  leadIn: { seconds: number; beats: number | null },
  pickupBeats = 0,
): { totalSeconds: number; secondsPerBeat: number | null } {
  const rawSecondsPerBeat = leadIn.beats !== null ? leadIn.seconds / leadIn.beats : null;
  const effectiveBeats = leadIn.beats !== null ? (pickupBeats >= leadIn.beats ? leadIn.beats : leadIn.beats - pickupBeats) : null;
  const totalSeconds = effectiveBeats !== null && rawSecondsPerBeat !== null ? effectiveBeats * rawSecondsPerBeat : leadIn.seconds;
  return { totalSeconds, secondsPerBeat: rawSecondsPerBeat };
}

export type EngineKind = 'file' | 'metronome' | 'none';
export type ControllerStatus = 'READY' | 'PLAYING' | 'PAUSED' | 'STOPPED' | 'ERROR' | 'ENDED' | 'PRE_ROLLING';

export interface PreRollInfo {
  /** Total pre-roll duration in seconds, fixed for this count-in. */
  totalSeconds: number;
  /** Ticks down to 0 in real time; the file starts the instant this hits 0. */
  remainingSeconds: number;
  /** Set only when the pre-roll was configured as a beat-count (synthetic
   * click enabled) — lets the UI display "8, 7, 6...1" instead of raw
   * seconds, same convention as the marker-tap lead-in countdown. */
  secondsPerBeat: number | null;
}

export interface ControllerState {
  status: ControllerStatus;
  activeEngine: EngineKind;
  currentTime: number;
  duration: number;
  errorMessage: string | null;
  /** True while the currently loaded song has its synthetic click track
   * turned on — surfaced so Performance mode can show a small "SYN CLICK"
   * indicator distinguishing it from a real click baked into the file. */
  hasSyntheticClick: boolean;
  /** Non-null only while status is 'PRE_ROLLING' — a count-in playing out
   * before the file itself starts, for a song at (or restarted to) 0:00.
   * See play()'s own doc comment for why this exists as a distinct
   * mechanism from the marker-tap lead-in's seek-based approach. */
  preRoll: PreRollInfo | null;
  /** Practice-speed multiplier applied to the file engine (and, if active,
   * the synthetic click) — 1 is normal speed. Always reset to 1 by
   * loadSong(), never carried over from one song to the next; see
   * setPlaybackRate's own doc comment for the full safety reasoning. Stays
   * 1 (and setPlaybackRate is a no-op) for a metronome-mode song — there's
   * no audio to time-stretch, and that engine's own BPM slider already
   * covers "slower." */
  playbackRate: number;
}

type Listener = (state: ControllerState) => void;

export class PlaybackController {
  private audioContext: AudioContext;
  private fileEngine: FilePlaybackEngine;
  private metronomeEngine: MetronomeEngine | null = null;
  private activeEngine: EngineKind = 'none';
  private state: ControllerState = {
    status: 'READY',
    activeEngine: 'none',
    currentTime: 0,
    duration: 0,
    errorMessage: null,
    hasSyntheticClick: false,
    preRoll: null,
    playbackRate: 1,
  };
  private listeners = new Set<Listener>();
  private beatListeners = new Set<(beat: BeatEvent) => void>();
  private unsubscribeFile: (() => void) | null = null;
  private unsubscribeMetronome: (() => void) | null = null;
  /** Guards against rapid double-taps launching overlapping start operations. */
  private transitionInFlight = false;
  private loadedSongId: string | null = null;
  /** True only right after loadSong() or stop() — a genuine "song is at the
   * top, about to (re)start" moment — NOT after seek()/reanchor lands on
   * 0:00 as a side effect (e.g. a marker-tap lead-in clamping backward
   * because the marker sits very close to the start). Without this
   * distinction, tapping a marker whose lead-in clamps to 0:00 would make
   * the NEXT play() also fire a pre-roll, stacking an unrelated count-in on
   * top of the lead-in that already just happened. Only loadSong()/stop()
   * set this true; seek() (and thus every marker tap) always clears it. */
  private eligibleForPreRoll = false;
  /** Ticks the pre-roll countdown and fires the actual fileEngine.play() once
   * it reaches 0; cleared on pause/stop/seek/new-song so a stale pre-roll
   * can never fire after the user has moved on from it. */
  private preRollTimer: ReturnType<typeof setInterval> | null = null;

  // Runs ALONGSIDE the file engine (not an alternative to it, unlike
  // metronomeEngine/fileEngine's mutual exclusivity) — a song can be
  // "Tracks + Click" or "Tracks Only" AND have a synthetic click layered on
  // top at the same time, since this is an independent opt-in per song, not
  // a playback mode of its own.
  private clickLayer: SyntheticClickLayer | null = null;
  private clickEnabled = false;
  private clickOffsetSeconds = 0;

  /** Browsers can suspend/throttle a backgrounded tab's AudioContext and
   * timers to varying, unpredictable degrees (there is no single documented
   * guarantee across Chrome/Firefox/Safari/Android WebView) — with nothing
   * to repair it, tapping Play/Pause after returning to the tab could look
   * "frozen" until some OTHER action happened to route through play()'s own
   * resume-if-suspended check. This listener runs the moment the tab
   * becomes visible again, independent of any user action, so returning to
   * the app is never a dead end. */
  private visibilityHandler = () => {
    if (document.visibilityState !== 'visible') return;
    if (this.audioContext.state === 'suspended') void this.audioContext.resume();
    // A backgrounded tab can throttle setInterval enough that the pre-roll
    // countdown's own 50ms tick never got a chance to notice it had already
    // finished — completion is keyed off the ABSOLUTE AudioContext clock
    // (see startPreRoll), so if time has genuinely passed the countdown's
    // total, finish it immediately rather than leaving it stuck reporting a
    // countdown that will never reach zero on its own. Safe to call
    // unconditionally — it's a no-op whenever activePreRoll is null (no
    // pre-roll in flight).
    this.checkPreRollCompletion();
  };

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
    this.fileEngine = new FilePlaybackEngine(audioContext);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  getState(): ControllerState {
    return this.state;
  }

  /** Subscribe to metronome beat events; no-op events while the file engine is active. */
  subscribeBeats(listener: (beat: BeatEvent) => void): () => void {
    this.beatListeners.add(listener);
    return () => this.beatListeners.delete(listener);
  }

  /** The synthetic click's own notion of "current file time," for anything
   * visualizing the click live (e.g. Performance mode's beat-flash
   * indicator) — see SyntheticClickLayer.currentFileTime's own doc comment
   * for why this must be used instead of state.currentTime (sourced from
   * HTMLMediaElement, a different clock than the AudioContext one the click
   * is actually scheduled against). Also correctly reports NEGATIVE file
   * time during a pre-roll count-in (the click is already running, counting
   * up toward the song's real start) — state.currentTime has no equivalent
   * concept at all during PRE_ROLLING, since the file engine hasn't started
   * yet. Returns null when there's no click layer running to ask at all. */
  currentClickFileTime(): number | null {
    if (!this.clickEnabled || !this.clickLayer) return null;
    return this.clickLayer.currentFileTime();
  }

  /** Current file time from the active media element, for UI work that
   * needs a live readout instead of the last controller snapshot. */
  currentFileTime(): number {
    return this.fileEngine.currentTime;
  }

  private setState(partial: Partial<ControllerState>): void {
    this.state = { ...this.state, ...partial };
    for (const listener of this.listeners) listener(this.state);
  }

  /** Pushes updated click settings (sound, accent, volume, BPM, etc.) into
   * whichever engine is currently loaded for THIS song, without reloading
   * it — loadSong() captures song.metronomeSettings once, at construction
   * time, and neither engine watches the store for changes on its own,
   * so a settings edit made while the song is already loaded (e.g. picking
   * a different click-sound preset from the Player) would otherwise have
   * no audible effect until the song was reloaded from scratch. Both
   * MetronomeEngine and SyntheticClickLayer already support a live
   * settings swap (their own updateSettings) — this just routes to
   * whichever one is actually active, mid-playback included. No-ops
   * harmlessly if neither engine is currently instantiated. */
  updateMetronomeSettings(settings: MetronomeSettings): void {
    this.metronomeEngine?.updateSettings(settings);
    this.clickLayer?.updateSettings(settings);
  }

  static readonly MIN_PLAYBACK_RATE = 0.5;
  static readonly MAX_PLAYBACK_RATE = 1.2;

  /** Rehearsal-only practice control — changes a loaded song's playback
   * speed while keeping its pitch unchanged (see
   * FilePlaybackEngine.setPlaybackRate), for practicing along to a hard
   * section slowed down, or drilling a part faster than performance tempo
   * to build technique, without the pitch shifting either way the way
   * naive resampling would. No-op for a metronome-mode song (no audio to
   * stretch — its own BPM slider already covers changing tempo directly).
   * Clamped to [0.5, 1.2] — SyntheticClickLayer's rate-scaling math is
   * symmetric (see its own setRate doc comment) and verified by its own
   * tests at rates on both sides of 1, but there's no tested/UI-exposed
   * reason to go further in either direction than this range.
   *
   * Always reset back to 1 by loadSong() (see there) so a practice speed
   * can never silently carry over onto the NEXT song, or survive a
   * setlist being switched from Rehearsal to Live mid-session —
   * Performance mode's own UI additionally hides the control outside
   * Rehearsal mode, but this reset is the actual safety backstop,
   * independent of whether the UI remembered to hide it. */
  setPlaybackRate(rate: number): void {
    if (this.activeEngine !== 'file') return;
    const clamped = Math.min(PlaybackController.MAX_PLAYBACK_RATE, Math.max(PlaybackController.MIN_PLAYBACK_RATE, rate));
    this.fileEngine.setPlaybackRate(clamped);
    if (this.clickEnabled) this.clickLayer?.setRate(clamped);
    this.setState({ playbackRate: clamped });
  }

  /** Loads a song for the given mode without starting playback. */
  async loadSong(song: SongRecord, routing: SetlistRouting = 'rehearsal-track-stereo-click-stereo'): Promise<void> {
    if (this.transitionInFlight) return;
    this.transitionInFlight = true;
    try {
      this.stopPreRoll();
      this.teardownActiveEngine();
      this.teardownClickLayer();
      this.loadedSongId = song.id;
      this.eligibleForPreRoll = true;

      if (song.mode === 'metronome') {
        const { clickSide } = resolveOutputSides(routing, false, true);
        this.activeEngine = 'metronome';
        this.metronomeEngine = new MetronomeEngine(this.audioContext, { ...song.metronomeSettings, clickChannel: clickSide });
        this.unsubscribeMetronome = this.metronomeEngine.subscribe((event) => this.handleMetronomeEvent(event));
        this.setState({
          status: 'READY',
          activeEngine: 'metronome',
          currentTime: 0,
          duration: 0,
          errorMessage: null,
          hasSyntheticClick: false,
          preRoll: null,
          playbackRate: 1,
        });
        return;
      }

      const audioRecord = await database.getAudioBlob(song.id);
      if (!audioRecord) {
        this.setState({
          status: 'ERROR',
          activeEngine: 'none',
          errorMessage: 'No audio file is stored for this song. Re-import it in the Song Library.',
          hasSyntheticClick: false,
          preRoll: null,
          playbackRate: 1,
        });
        return;
      }

      this.activeEngine = 'file';
      this.unsubscribeFile = this.fileEngine.subscribe((event) => this.handleFileEvent(event));
      // The setlist's routing choice plus this song's own content (does it
      // have an active click at all?) collapse into real output sides via
      // resolveOutputSides — see its own doc comment. Routing is no longer
      // read from the song itself; a song only ever contributes whether it
      // HAS a click to split from its track.
      const { trackSide, clickSide } = resolveOutputSides(routing, true, song.syntheticClickEnabled);
      await this.fileEngine.load(audioRecord.blob, trackSide);

      this.clickEnabled = song.syntheticClickEnabled;
      this.clickOffsetSeconds = song.syntheticClickOffsetSeconds;
      if (this.clickEnabled) {
        this.clickLayer = new SyntheticClickLayer(this.audioContext, song.metronomeSettings, clickSide);
      }

      this.setState({
        status: 'READY',
        activeEngine: 'file',
        currentTime: 0,
        duration: this.fileEngine.duration || 0,
        errorMessage: null,
        hasSyntheticClick: this.clickEnabled,
        preRoll: null,
        playbackRate: 1,
      });
    } catch (error) {
      this.setState({
        status: 'ERROR',
        errorMessage: error instanceof Error ? error.message : 'Failed to load song.',
      });
    } finally {
      this.transitionInFlight = false;
    }
  }

  /** A precise, near-zero threshold rather than an exact `=== 0` check —
   * float rounding from prior seeks/stops could leave currentTime at
   * something like 1e-8 instead of exactly 0. */
  private static readonly START_OF_SONG_THRESHOLD_SECONDS = 0.05;

  /** `leadIn` describes the CURRENTLY configured marker lead-in (same
   * settings used for marker-tap seeks) — pass it so a song starting from
   * 0:00 gets an equivalent count-in before its own audio starts, since a
   * seek-based lead-in has nothing to seek "before the start of the song"
   * into. Omit (or pass null) to play with no pre-roll regardless of
   * position, e.g. for the standalone Metronome tab or any caller that
   * doesn't want this behavior. `pickupBeats` is the CURRENT song's own
   * pickup/silence-before-first-beat count (only meaningful when `leadIn`
   * is beat-based) — subtracted from the chosen beat-count so a song with,
   * say, 5 pickup beats baked in and an 8-count chosen still lands its own
   * first beat exactly 8 beats after the pre-roll starts, instead of 8+5.
   * `firstBeatTimeSeconds` (if the song has one marked) is where the file
   * actually starts playing once the pre-roll count-in finishes — the
   * count-in's own beat count already accounts for the pickup material via
   * `pickupBeats`, so skipping straight to the real first beat means the
   * band never sits through the pickup section's silence/count-in beats
   * twice (once from the click, once from the song itself starting cold at
   * 0:00). Null means "no marked first beat" — starts from 0:00 exactly as
   * before. */
  async play(
    leadIn?: { seconds: number; beats: number | null } | null,
    pickupBeats = 0,
    firstBeatTimeSeconds: number | null = null,
  ): Promise<void> {
    if (this.transitionInFlight) return;
    if (this.state.status === 'PLAYING' || this.state.status === 'PRE_ROLLING') return; // ignore repeated taps
    this.transitionInFlight = true;
    try {
      // Resume once, up front, before ANY branch below schedules audio.
      // Bug fix: the pre-roll/count-in path (startPreRoll) calls
      // clickLayer.start() directly and previously never resumed the
      // context itself — MetronomeEngine.start() and FileEngine.play() each
      // have their own internal resume() call, but startPreRoll has neither,
      // so on a song's very first-ever Play tap (a fresh AudioContext,
      // still 'suspended' per browser autoplay policy until a user gesture
      // resumes it) the pre-roll's click buffers got scheduled against a
      // context that wasn't actually running yet. Web Audio silently drops
      // audio scheduled while suspended rather than queuing it — the
      // buffers' start() calls "succeed" and the visual beat-flash (timed
      // off the same scheduledTime math regardless of suspension) fires
      // right on schedule, but the FIRST click(s) of the count-in are
      // genuinely inaudible. Resuming here, before any branch, fixes it for
      // all three paths uniformly instead of patching startPreRoll alone.
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      if (this.activeEngine === 'metronome' && this.metronomeEngine) {
        await this.metronomeEngine.start();
        this.setState({ status: 'PLAYING', errorMessage: null });
      } else if (this.activeEngine === 'file') {
        const isAtStart = this.fileEngine.currentTime <= PlaybackController.START_OF_SONG_THRESHOLD_SECONDS;
        // eligibleForPreRoll is NOT consumed here — only seek() clears it.
        // That means a cancelled pre-roll (paused mid-count-in) can be
        // retried by tapping Play again, since the song is still sitting at
        // the same genuine restart position; it only stops being eligible
        // once something actually seeks (including a marker-tap lead-in
        // that happens to clamp back to 0:00, which is exactly the case
        // this flag exists to distinguish from a real restart).
        //
        // this.state.playbackRate === 1: the pre-roll countdown
        // (checkPreRollCompletion) measures its own totalSeconds against
        // the ABSOLUTE AudioContext clock, in real time — it has no concept
        // of a rate-stretched click, unlike SyntheticClickLayer's own
        // scheduling math (see its setRate doc comment). Rather than teach
        // a second, independent timer about the same stretch factor, a
        // practice-speed session simply skips the pre-roll and starts
        // playing immediately (still at the chosen rate, click included) —
        // a marker tap's lead-in remains available since that one is
        // purely seek-based and already rate-correct.
        if (isAtStart && this.eligibleForPreRoll && leadIn && leadIn.seconds > 0 && this.state.playbackRate === 1) {
          this.startPreRoll(leadIn, pickupBeats, firstBeatTimeSeconds);
        } else {
          await this.fileEngine.play();
          if (this.clickEnabled) this.clickLayer?.start(this.fileEngine.currentTime, this.clickOffsetSeconds);
          this.setState({ status: 'PLAYING', errorMessage: null });
        }
      }
    } catch (error) {
      this.setState({ status: 'ERROR', errorMessage: error instanceof Error ? error.message : 'Playback failed.' });
    } finally {
      this.transitionInFlight = false;
    }
  }

  /** Plays a count-in BEFORE the file's own audio starts, for a song at
   * 0:00 — the marker-tap lead-in works by seeking backward from a marker
   * into audio that already exists, which has nothing to borrow "before the
   * start of the song." This instead holds the file silent and (if the
   * song has a synthetic click) plays real click beats counting UP to file
   * time 0, using the exact same anchor math SyntheticClickLayer already
   * uses for marker seeks — the anchor point is just 0 instead of a marker
   * timestamp, and the "current position" fed in is negative (seconds
   * before the start) rather than a real file position. A plain seconds
   * lead-in with no synthetic click still holds silence for that long, so
   * every lead-in unit (seconds or beats) gets equivalent pre-roll
   * treatment, just without an audible click to count by.
   *
   * `pickupBeats` (the song's own pickup/silence count) is only meaningful
   * when the lead-in is beat-based — a plain-seconds lead-in has no BPM to
   * convert pickup beats against, so it passes through unchanged. The
   * chosen beat-count is reduced by pickupBeats, then converted back to
   * seconds at the same per-beat rate, so the song's real first beat still
   * lands exactly where an unmodified count would have placed it. If
   * pickupBeats is as large or larger than the chosen count itself (a song
   * with a long pickup phrase and a short lead-in chosen, e.g. 13 pickup
   * beats against an 8-count), there's nothing left to reduce — the full
   * chosen count-in plays unreduced, rather than collapsing toward a
   * near-instant sliver that would look and feel broken.
   *
   * `firstBeatTimeSeconds`, if set, is where the file actually starts once
   * the count-in reaches zero — the click's own rendezvous anchor shifts to
   * that same position (instead of file-time 0) so the hand-off stays
   * seamless: the click counts UP TO `firstBeatTimeSeconds`, and the file is
   * seeked there and started at the exact same moment, rather than the
   * click counting into 0:00 while the file jumps somewhere else. */
  /** Snapshot of the in-flight pre-roll's own parameters — kept as instance
   * state (not just locals closed over by startPreRoll's interval) so
   * checkPreRollCompletion can be called from a SECOND place
   * (visibilityHandler) using the exact same totalSeconds/startedAt/
   * firstBeatTimeSeconds a throttled/stalled interval tick would have used,
   * rather than duplicating this math. Null whenever no pre-roll is in
   * flight. */
  private activePreRoll: {
    totalSeconds: number;
    secondsPerBeat: number | null;
    startedAt: number;
    firstBeatTimeSeconds: number | null;
  } | null = null;

  private startPreRoll(
    leadIn: { seconds: number; beats: number | null },
    pickupBeats = 0,
    firstBeatTimeSeconds: number | null = null,
  ): void {
    const { totalSeconds, secondsPerBeat: secondsPerBeatValue } = computeCountInPreview(leadIn, pickupBeats);
    const playbackStartSeconds = firstBeatTimeSeconds ?? 0;

    if (this.clickEnabled && this.clickLayer) {
      // "Now" is `totalSeconds` before the song's rendezvous point — the
      // click engine's own grid math (anchored at `playbackStartSeconds`,
      // same as a marker seek there) naturally produces beats counting UP
      // from here to that point, where the file then takes over exactly on
      // the beat.
      this.clickLayer.start(playbackStartSeconds - totalSeconds, this.clickOffsetSeconds);
    }

    this.activePreRoll = { totalSeconds, secondsPerBeat: secondsPerBeatValue, startedAt: this.audioContext.currentTime, firstBeatTimeSeconds };
    this.setState({
      status: 'PRE_ROLLING',
      errorMessage: null,
      preRoll: { totalSeconds, remainingSeconds: totalSeconds, secondsPerBeat: secondsPerBeatValue },
    });

    this.preRollTimer = setInterval(() => this.checkPreRollCompletion(), 50);
  }

  /** Checks the in-flight pre-roll against the ABSOLUTE AudioContext clock
   * and either ticks the displayed countdown or completes it — called both
   * by the normal 50ms interval tick AND by visibilityHandler on returning
   * to a backgrounded tab (see its own doc comment for why: a throttled
   * interval might not have gotten a chance to notice completion on its
   * own, even though the actual deadline has already passed). Keying
   * everything off `this.activePreRoll` (not local closure variables) is
   * what makes calling this from two different places safe — both see the
   * exact same startedAt/totalSeconds. No-ops if nothing is actually
   * in-flight (already completed via one path before the other ran, or
   * cancelled outright by stopPreRoll). */
  private checkPreRollCompletion(): void {
    const active = this.activePreRoll;
    if (!active) return;
    const { totalSeconds, secondsPerBeat: secondsPerBeatValue, startedAt, firstBeatTimeSeconds } = active;
    const elapsed = this.audioContext.currentTime - startedAt;
    const remaining = Math.max(0, totalSeconds - elapsed);
    if (remaining <= 0) {
      // Only clear the TIMER here, not the click layer — on natural
      // completion the click must keep playing seamlessly straight into
      // the file's own first beat, not cut off right as the song starts.
      // Cancellation (user paused/sought/switched songs mid-count-in)
      // goes through the separate stopPreRoll() below instead, which does
      // stop the click, since there's no "song starting" to hand off to.
      this.clearPreRollTimer();
      this.activePreRoll = null;
      // Skip straight to the marked first beat (if any) rather than
      // starting cold at 0:00 — the count-in's own beat count already
      // accounted for the pickup material via pickupBeats, so playing
      // through it again from 0:00 would count it twice.
      if (firstBeatTimeSeconds !== null) {
        this.fileEngine.seek(firstBeatTimeSeconds);
        this.setState({ currentTime: this.fileEngine.currentTime });
      }
      void this.fileEngine.play().then(() => {
        // The click is already running (started above, anchored through
        // the same rendezvous point) — no separate clickLayer.start() call
        // here, unlike the plain play() path, or it would re-anchor and
        // audibly glitch right on the downbeat it was counting into.
        this.setState({ status: 'PLAYING', errorMessage: null, preRoll: null });
      });
      return;
    }
    this.setState({ preRoll: { totalSeconds, remainingSeconds: remaining, secondsPerBeat: secondsPerBeatValue } });
  }

  private clearPreRollTimer(): void {
    if (this.preRollTimer !== null) {
      clearInterval(this.preRollTimer);
      this.preRollTimer = null;
    }
  }

  /** Cancels an in-flight pre-roll (pause/stop/seek/new-song tapped during
   * the count-in) without letting the timer's own fileEngine.play() fire
   * afterward — otherwise the song could start playing seconds after the
   * user thought they'd stopped it. Unlike natural completion, this also
   * stops the click layer: there's no song starting to hand off to, so the
   * click has nothing left to count into. */
  private stopPreRoll(): void {
    const wasPreRolling = this.state.status === 'PRE_ROLLING';
    this.clearPreRollTimer();
    this.activePreRoll = null;
    if (wasPreRolling) {
      this.clickLayer?.stop();
    }
  }

  pause(): void {
    if (this.state.status === 'PRE_ROLLING') {
      this.stopPreRoll();
      this.setState({ status: 'PAUSED', preRoll: null });
      return;
    }
    if (this.state.status !== 'PLAYING') return;
    if (this.activeEngine === 'metronome') {
      this.metronomeEngine?.pause();
      this.setState({ status: 'PAUSED' });
    } else if (this.activeEngine === 'file') {
      this.fileEngine.pause();
      this.clickLayer?.stop();
      this.setState({ status: 'PAUSED' });
    }
  }

  stop(): void {
    this.stopPreRoll();
    if (this.activeEngine === 'metronome') {
      this.metronomeEngine?.stop();
    } else if (this.activeEngine === 'file') {
      this.fileEngine.stop();
      this.clickLayer?.stop();
    }
    this.eligibleForPreRoll = true;
    this.setState({ status: 'STOPPED', currentTime: 0, preRoll: null });
  }

  /** Seeks the loaded file to a timestamp without changing play/pause state.
   * No-op for the metronome engine, which has no meaningful timeline to seek.
   * Re-anchors the synthetic click (if enabled and currently playing) to the
   * new position — same "marker-tap seeks must keep the click in sync"
   * requirement as the lead-in feature, since a seek without this would
   * leave the click referencing wherever the file used to be. Cancels an
   * in-flight pre-roll: seeking implies a specific new target the drummer
   * wants right now, not "count in from the top" — same reasoning as a
   * manual scrub already voiding a pending marker lead-in elsewhere. Also
   * clears eligibility for a FUTURE pre-roll: if a marker-tap lead-in
   * clamps all the way back to 0:00 (marker very close to the start), that
   * must not make the next play() fire an unrelated pre-roll stacked on
   * top of the lead-in that just happened — only a genuine fresh load or
   * explicit restart (loadSong()/stop()) re-enables it. */
  seek(timeSeconds: number): void {
    if (this.activeEngine !== 'file') return;
    if (this.state.status === 'PRE_ROLLING') {
      this.stopPreRoll();
      this.setState({ status: 'PAUSED', preRoll: null });
    }
    this.eligibleForPreRoll = false;
    this.fileEngine.seek(timeSeconds);
    if (this.clickEnabled) this.clickLayer?.reanchor(this.fileEngine.currentTime);
    this.setState({ currentTime: this.fileEngine.currentTime });
  }

  private handleFileEvent(event: FilePlaybackEvent): void {
    switch (event.type) {
      case 'timeupdate':
        this.setState({ currentTime: event.currentTime, duration: event.duration });
        break;
      case 'ended':
        this.setState({ status: 'ENDED', currentTime: this.fileEngine.duration });
        break;
      case 'error':
        this.setState({ status: 'ERROR', errorMessage: event.message });
        break;
      case 'playing':
      case 'pause':
        break;
    }
  }

  private handleMetronomeEvent(event: MetronomeEvent): void {
    if (event.type === 'stopped' && this.state.status === 'PLAYING') {
      this.setState({ status: 'STOPPED' });
    } else if (event.type === 'beat') {
      for (const listener of this.beatListeners) listener(event.beat);
    }
  }

  private teardownActiveEngine(): void {
    if (this.activeEngine === 'file') {
      this.fileEngine.stop();
      this.fileEngine.teardown();
    } else if (this.activeEngine === 'metronome') {
      this.metronomeEngine?.teardown();
      this.metronomeEngine = null;
    }
    this.unsubscribeFile?.();
    this.unsubscribeFile = null;
    this.unsubscribeMetronome?.();
    this.unsubscribeMetronome = null;
    this.activeEngine = 'none';
    this.loadedSongId = null;
  }

  /** Independent of teardownActiveEngine — the click layer is its own thing
   * running alongside the file engine, not one of the mutually-exclusive
   * engine kinds, so it needs its own explicit teardown call at every point
   * a new song is loaded (or the controller itself is torn down). */
  private teardownClickLayer(): void {
    this.clickLayer?.teardown();
    this.clickLayer = null;
    this.clickEnabled = false;
    this.clickOffsetSeconds = 0;
  }

  get currentSongId(): string | null {
    return this.loadedSongId;
  }

  teardown(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.stopPreRoll();
    this.teardownActiveEngine();
    this.teardownClickLayer();
  }
}
